import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import PopupMessage from "../../components/common/PopUpMessage";
import "../../assets/css/GmailSetup.css";
import EmailService from "../../services/EmailService";
import LeadService from "../../services/LeadService";
import ApiClient from "../../ApiClient";

const SWEET_ALERT_CONFIRM_COLOR = "rgb(208 125 63)";
const GMAIL_PROVIDER = "Gmail";
const ZOHO_PROVIDER = "Zoho Mail";
const SMTP_PROVIDER = "Other";
const EMAIL_PROVIDERS = [GMAIL_PROVIDER, ZOHO_PROVIDER, SMTP_PROVIDER];
const EMAIL_TYPE_BY_PROVIDER = {
  [GMAIL_PROVIDER]: "gmail",
  [ZOHO_PROVIDER]: "zoho",
};
const PROVIDER_IMAGE_BY_PROVIDER = {
  [GMAIL_PROVIDER]: "gmail",
  [ZOHO_PROVIDER]: "zoho",
  [SMTP_PROVIDER]: "other",
};
const GMAIL_CLIENT_ID = process.env.REACT_APP_GMAIL_CLIENT_ID;
const GMAIL_REDIRECT_URI =
  process.env.REACT_APP_GMAIL_REDIRECT_URI ||
  `${window.location.origin}/google-email-callback.html`;
const GMAIL_SCOPE =
  "openid email profile https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly";

const getGmailOAuthUrl = () => {
  if (!GMAIL_CLIENT_ID) throw new Error("Gmail OAuth client ID is not configured.");
  return (
    "https://accounts.google.com/o/oauth2/v2/auth" +
    "?response_type=token" +
    `&client_id=${GMAIL_CLIENT_ID}` +
    `&redirect_uri=${encodeURIComponent(GMAIL_REDIRECT_URI)}` +
    `&scope=${encodeURIComponent(GMAIL_SCOPE)}` +
    "&prompt=select_account" +
    `&state=${encodeURIComponent(window.location.origin)}`
  );
};

const ZOHO_CLIENT_ID = process.env.REACT_APP_ZOHO_CLIENT_ID;
const ZOHO_AUTH_URL = "https://accounts.zoho.com/oauth/v2/auth";
const ZOHO_REDIRECT_URI =
  process.env.REACT_APP_ZOHO_REDIRECT_URI ||
  `${window.location.origin}/zoho-oauth-callback.html`;
const ZOHO_SCOPE = "ZohoMail.messages.READ,ZohoMail.messages.CREATE,ZohoMail.messages.UPDATE,ZohoMail.messages.DELETE,ZohoMail.accounts.READ,ZohoMail.folders.READ";

const getApiErrorMessage = (error) => {
  const responseData = error?.response?.data ?? error?.data ?? error;

  return (
    responseData?.error ??
    responseData?.message ??
    error?.message ??
    ""
  );
};

const isEmailUnauthorizedError = (error) => {
  const status = error?.response?.status ?? error?.status;
  const errorMessage = String(getApiErrorMessage(error)).toLowerCase();

  return (
    status === 401 ||
    errorMessage.includes("unauthorized") ||
    errorMessage.includes("invalid authentication credentials")
  );
};

const getZohoOAuthUrl = () => {
  if (!ZOHO_CLIENT_ID) {
    throw new Error("Zoho OAuth client ID is not configured.");
  }

  return (
    `${ZOHO_AUTH_URL}` +
    `?response_type=code` +
    `&client_id=${ZOHO_CLIENT_ID}` +
    `&redirect_uri=${encodeURIComponent(ZOHO_REDIRECT_URI)}` +
    `&scope=${ZOHO_SCOPE}` +
    `&access_type=offline` +
    `&prompt=consent` +
    `&state=${encodeURIComponent(window.location.origin)}`
  );
};

const normalizeGroupSuggestions = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];

  if (!Array.isArray(payload)) return [];

  const groupsByKey = new Map();

  payload.forEach((group, index) => {
    const groupId =
      group?.groupId ??
      group?.GroupId ??
      group?.leadGroupId ??
      group?.LeadGroupId ??
      group?.id ??
      group?.Id ??
      "";
    const groupName =
      group?.groupName ??
      group?.GroupName ??
      group?.name ??
      group?.Name ??
      "";
    const leadItems =
      group?.associatedLeads ??
      group?.AssociatedLeads ??
      group?.leads ??
      group?.Leads ??
      group?.leadDetails ??
      group?.LeadDetails ??
      [];
    const normalizedLeads = (
      Array.isArray(leadItems) && leadItems.length > 0 ? leadItems : [group]
    )
      .map((lead, leadIndex) => ({
        leadId:
          lead?.leadId ??
          lead?.LeadId ??
          lead?.id ??
          lead?.Id ??
          `${groupId || groupName || index}-lead-${leadIndex}`,
        leadName:
          lead?.leadName ??
          lead?.LeadName ??
          lead?.name ??
          lead?.Name ??
          "",
        email: lead?.email ?? lead?.Email ?? "",
      }))
      .filter((lead) => lead.email);

    if (!groupName || normalizedLeads.length === 0) return;

    const groupKey = String(groupId || groupName);
    const existingGroup = groupsByKey.get(groupKey);

    if (existingGroup) {
      const existingEmails = new Set(
        existingGroup.leads.map((lead) => lead.email.toLowerCase()),
      );
      normalizedLeads.forEach((lead) => {
        const normalizedEmail = lead.email.toLowerCase();
        if (!existingEmails.has(normalizedEmail)) {
          existingGroup.leads.push(lead);
          existingEmails.add(normalizedEmail);
        }
      });
      return;
    }

    groupsByKey.set(groupKey, {
      groupId: groupId || groupKey,
      groupName,
      leads: normalizedLeads,
    });
  });

  return Array.from(groupsByKey.values());
};

const encryptPassword = (password) => {
  if (!password) return "";
  try {
    return btoa(unescape(encodeURIComponent(password)));
  } catch (e) {
    console.error("Encryption failed:", e);
    return password;
  }
};

const decryptPassword = (encryptedPassword) => {
  if (!encryptedPassword) return "";
  try {
    return decodeURIComponent(escape(atob(encryptedPassword)));
  } catch (e) {
    try {
      return atob(encryptedPassword);
    } catch {
      return encryptedPassword;
    }
  }
};

const mapConfigToFormState = (config) => {
  if (!config) return {
    smtpServer: "",
    senderEmail: "",
    smtpPort: "",
    userName: "",
    senderName: "",
    password: "",
    isDefault: false,
    isActive: true,
  };

  const rawEncryptedPassword = config.encryptedPassword ?? config.EncryptedPassword ?? config.password ?? config.Password ?? "";

  return {
    id: config.id ?? config.Id,
    smtpServer: config.smtpServer ?? config.SmtpServer ?? "",
    senderEmail: config.senderEmail ?? config.SenderEmail ?? "",
    smtpPort: config.smtpPort ?? config.SmtpPort ?? "",
    userName: config.userName ?? config.UserName ?? "",
    senderName: config.senderName ?? config.SenderName ?? "",
    password: decryptPassword(rawEncryptedPassword),
    isDefault: config.isDefault ?? config.IsDefault ?? false,
    isActive: config.isActive ?? config.IsActive ?? true,
  };
};

const EmailSetup = () => {
  const emptySmtpForm = {
    smtpServer: "",
    senderEmail: "",
    smtpPort: "",
    userName: "",
    senderName: "",
    password: "",
    isDefault: false,
    isActive: true,
  };

  const [form, setForm] = useState({
    ...emptySmtpForm,
  });

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerTokens, setProviderTokens] = useState({
    gmail: null,
    zoho: null,
  });
  const [integratedProviders, setIntegratedProviders] = useState(new Set());

  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [selectedSmtpConfigId, setSelectedSmtpConfigId] = useState("");
  const [showSmtpForm, setShowSmtpForm] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);

  const [compose, setCompose] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupLeadSearch, setGroupLeadSearch] = useState("");
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionRequestIdRef = useRef(0);
  const recipientInputRef = useRef(null);

  // ✅ Common popup state
  const [popup, setPopup] = useState({ message: "", type: "success" });

  const getSelectedProviderToken = () => {
    const emailType = EMAIL_TYPE_BY_PROVIDER[selectedProvider];
    return emailType ? providerTokens[emailType] : null;
  };

  const fetchSmtpConfigs = async (autoSelectConfigId = null) => {
    setIsLoadingConfigs(true);
    try {
      const response = await EmailService.getSmtpConfigurations();
      const configs = Array.isArray(response.data?.data)
        ? response.data.data
        : (Array.isArray(response.data) ? response.data : []);
      setSmtpConfigs(configs);

      if (configs.length > 0) {
        if (autoSelectConfigId) {
          const configToSelect = configs.find(
            (c) => String(c.id ?? c.Id) === String(autoSelectConfigId)
          );
          if (configToSelect) {
            const configId = configToSelect.id ?? configToSelect.Id;
            setSelectedSmtpConfigId(configId);
            handleSmtpConfigSelect(configId);
            return;
          }
        }

        // Search for default SMTP config
        const defaultCfg = configs.find((c) => c.isDefault || c.IsDefault);
        if (defaultCfg) {
          const configId = defaultCfg.id ?? defaultCfg.Id;
          setSelectedSmtpConfigId(configId);
          await handleSmtpConfigSelect(configId);
        } else {
          const firstId = configs[0].id ?? configs[0].Id;
          setSelectedSmtpConfigId(firstId);
          await handleSmtpConfigSelect(firstId);
        }
      } else {
        setShowSmtpForm(true);
        setForm(emptySmtpForm);
      }
    } catch (error) {
      console.error("Error fetching SMTP configurations:", error);
      setPopup({ message: "Failed to fetch SMTP configurations.", type: "error" });
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  const handleSmtpConfigSelect = async (configId) => {
    if (!configId) {
      setSelectedSmtpConfigId("");
      setForm(emptySmtpForm);
      return;
    }
    setSelectedSmtpConfigId(configId);
    setShowSmtpForm(false);
    try {
      const response = await EmailService.getSmtpConfigurationById(configId);
      const configDetail = response.data?.data ?? response.data;
      if (configDetail) {
        const mapped = mapConfigToFormState(configDetail);
        setForm(mapped);
        if (mapped.senderEmail) {
          EmailService.setActiveSmtpProvider(mapped.senderEmail, configId);
          setIntegratedProviders((prev) => new Set(prev).add(SMTP_PROVIDER));
        }
      }
    } catch (error) {
      console.error("Error fetching SMTP configuration detail:", error);
      setPopup({ message: "Failed to retrieve configuration details.", type: "error" });
    }
  };

  const handleAddNewSmtpClick = () => {
    setForm({
      ...emptySmtpForm,
      isDefault: false,
      isActive: true,
    });
    setSelectedSmtpConfigId("");
    setShowSmtpForm(true);
  };

  useEffect(() => {
    if (selectedProvider === SMTP_PROVIDER) {
      fetchSmtpConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  // ✅ On mount, check localStorage for email provider tokens
  useEffect(() => {
    const gmailToken = EmailService.getStoredGmailToken();
    const zohoToken = EmailService.getStoredZohoToken();
    
    let activeProv = null;
    try {
      activeProv = JSON.parse(localStorage.getItem("email_auth_active"));
    } catch {}

    setProviderTokens({
      gmail: gmailToken,
      zoho: zohoToken,
    });

    if (activeProv?.emailType === "smtp" || activeProv?.emailtype === "smtp") {
      setSelectedProvider(SMTP_PROVIDER);
      setIntegratedProviders((prev) => new Set(prev).add(SMTP_PROVIDER));
    } else if (gmailToken) {
      setSelectedProvider(GMAIL_PROVIDER);
      setIntegratedProviders((prev) => new Set(prev).add(GMAIL_PROVIDER));
    } else if (zohoToken) {
      setSelectedProvider((prev) => prev || ZOHO_PROVIDER);
      setIntegratedProviders((prev) => new Set(prev).add(ZOHO_PROVIDER));
    }
  }, []);

  useEffect(() => {
    const searchText = compose.to.trim();
    const requestId = suggestionRequestIdRef.current + 1;
    suggestionRequestIdRef.current = requestId;

    if (searchText.length < 3) {
      setGroupSuggestions([]);
      setShowSuggestions(false);
      setIsSuggestionLoading(false);
      return undefined;
    }

    setIsSuggestionLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await LeadService.getLeadAutoSuggestion(searchText);
        if (suggestionRequestIdRef.current !== requestId) return;

        const suggestions = normalizeGroupSuggestions(response);
        const selectedGroupIds = new Set(
          selectedGroups.map((group) => String(group.groupId)),
        );
        setGroupSuggestions(
          suggestions.filter(
            (suggestion) => !selectedGroupIds.has(String(suggestion.groupId)),
          ),
        );
        setShowSuggestions(true);
      } catch (error) {
        if (suggestionRequestIdRef.current !== requestId) return;

        console.error("Error fetching lead suggestions:", error);
        setGroupSuggestions([]);
        setShowSuggestions(false);
      } finally {
        if (suggestionRequestIdRef.current === requestId) {
          setIsSuggestionLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [compose.to, selectedGroups]);

  useEffect(() => {
    const handleGmailMessage = async (event) => {
      if (event.origin !== new URL(GMAIL_REDIRECT_URI).origin) return;

      if (event.data?.type === "GMAIL_OAUTH_SUCCESS") {
        const accessToken = event.data.accessToken;
        setSelectedProvider(GMAIL_PROVIDER);
        setProviderTokens((prev) => ({ ...prev, gmail: accessToken }));
        setIntegratedProviders((prev) => new Set(prev).add(GMAIL_PROVIDER));
        try {
          const userInfo = await EmailService.getGoogleUserInfo(accessToken);
          EmailService.storeGmailAuth(accessToken, userInfo.data?.email || '');
        } catch {
          EmailService.storeGmailAuth(accessToken, '');
        }
        setPopup({ message: "Gmail integrated successfully!", type: "success" });
      }

      if (event.data?.type === "GMAIL_OAUTH_ERROR") {
        setPopup({ message: event.data.message || "Failed to integrate Gmail.", type: "error" });
      }
    };

    window.addEventListener("message", handleGmailMessage);
    return () => window.removeEventListener("message", handleGmailMessage);
  }, []);

  const gmailLogin = () => {
    try {
      const popup = window.open(getGmailOAuthUrl(), "Gmail OAuth", "width=520,height=700");
      if (!popup) setPopup({ message: "Please allow popups to integrate Gmail.", type: "error" });
    } catch (e) {
      setPopup({ message: e.message || "Failed to start Gmail integration.", type: "error" });
    }
  };

  useEffect(() => {
    const handleZohoOAuthMessage = async (event) => {
      if (event.origin !== new URL(ZOHO_REDIRECT_URI).origin) return;

      if (event.data?.type === "ZOHO_OAUTH_CODE") {
        try {
          const resp = await ApiClient.post('/Email/zoho-exchange-token', {
            code: event.data.code,
            redirectUri: ZOHO_REDIRECT_URI,
          });
          const accessToken = resp.data?.accessToken;
          if (!accessToken) throw new Error('No access token returned.');
          EmailService.storeZohoAuth(accessToken);
          setSelectedProvider(ZOHO_PROVIDER);
          setProviderTokens((prev) => ({ ...prev, zoho: accessToken }));
          setIntegratedProviders((prev) => new Set(prev).add(ZOHO_PROVIDER));
          setPopup({ message: "Zoho Mail integrated successfully!", type: "success" });
        } catch (e) {
          setPopup({
            message: e.response?.data?.error || "Failed to integrate Zoho Mail.",
            type: "error",
          });
        }
      }

      if (event.data?.type === "ZOHO_OAUTH_ERROR") {
        setPopup({
          message: event.data.message || "Failed to integrate Zoho Mail.",
          type: "error",
        });
      }
    };

    window.addEventListener("message", handleZohoOAuthMessage);
    return () => window.removeEventListener("message", handleZohoOAuthMessage);
  }, []);

  const zohoLogin = () => {
    try {
      const url = getZohoOAuthUrl();
      const popupWindow = window.open(
        url,
        "Zoho OAuth",
        "width=520,height=700"
      );

      if (!popupWindow) {
        setPopup({
          message: "Please allow popups to integrate Zoho Mail.",
          type: "error",
        });
      }
    } catch (error) {
      setPopup({
        message: error.message || "Failed to start Zoho Mail integration.",
        type: "error",
      });
    }
  };

  // ✅ Handle provider selection
  const handleProviderClick = (provider) => {
    setSelectedProvider(provider);

    // clear compose form whenever switching provider
    setCompose({ to: "", subject: "", body: "" });
    setSelectedGroups([]);
    setActiveGroupId(null);
    setGroupLeadSearch("");
    setGroupSuggestions([]);
    setShowSuggestions(false);

    if (provider === GMAIL_PROVIDER) {
      if (providerTokens.gmail) {
        EmailService.setActiveEmailProvider('gmail');
        setPopup({
          message: "Gmail is already integrated and live.",
          type: "success",
        });
      } else {
        gmailLogin();
      }
    } else if (provider === ZOHO_PROVIDER) {
      if (providerTokens.zoho) {
        EmailService.setActiveEmailProvider('zoho');
        setPopup({
          message: "Zoho Mail is already integrated and live.",
          type: "success",
        });
      } else {
        zohoLogin();
      }
    } else if (provider === SMTP_PROVIDER) {
      if (smtpConfigs.length > 0) {
        setPopup({
          message: "SMTP configurations loaded. Choose a configuration below.",
          type: "success",
        });
      }
    }
  };

  const disconnectGmailIntegration = () => {
    EmailService.clearStoredEmailAuth("gmail");
    setProviderTokens((prev) => ({
      ...prev,
      gmail: null,
    }));
    setIntegratedProviders((prev) => {
      const next = new Set(prev);
      next.delete(GMAIL_PROVIDER);
      return next;
    });

    if (selectedProvider === GMAIL_PROVIDER) {
      setSelectedProvider(null);
    }
  };

  const disconnectZohoIntegration = () => {
    EmailService.clearStoredEmailAuth("zoho");
    setProviderTokens((prev) => ({
      ...prev,
      zoho: null,
    }));
    setIntegratedProviders((prev) => {
      const next = new Set(prev);
      next.delete(ZOHO_PROVIDER);
      return next;
    });

    if (selectedProvider === ZOHO_PROVIDER) {
      setSelectedProvider(null);
    }
  };

  const disconnectSmtpIntegration = () => {
    EmailService.clearStoredEmailAuth("smtp");
    localStorage.removeItem("email_auth_active");
    setSelectedSmtpConfigId("");
    setShowSmtpForm(false);
    setForm(emptySmtpForm);
    setIntegratedProviders((prev) => {
      const next = new Set(prev);
      next.delete(SMTP_PROVIDER);
      return next;
    });

    if (selectedProvider === SMTP_PROVIDER) {
      setSelectedProvider(null);
    }
  };

  const handleRemoveIntegration = async (e, provider) => {
    e.stopPropagation();

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Remove Integration?",
      text: `Remove your ${provider} email integration? You can integrate a new email again after this.`,
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: SWEET_ALERT_CONFIRM_COLOR,
    });

    if (!confirmation.isConfirmed) return;

    if (provider === GMAIL_PROVIDER) {
      disconnectGmailIntegration();
    }

    if (provider === ZOHO_PROVIDER) {
      disconnectZohoIntegration();
    }

    if (provider === SMTP_PROVIDER) {
      disconnectSmtpIntegration();
    }

    setCompose({ to: "", subject: "", body: "" });
    setSelectedGroups([]);
    setActiveGroupId(null);
    setGroupLeadSearch("");
    setGroupSuggestions([]);
    setShowSuggestions(false);

    setPopup({
      message: `${provider} integration removed successfully.`,
      type: "success",
    });
  };

  // ✅ Handle SMTP form changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRecipientChange = (e) => {
    setCompose({ ...compose, to: e.target.value });
    setActiveGroupId(null);
    setGroupLeadSearch("");
  };

  const handleGroupSelect = (group) => {
    const selectedLeadIds = new Set(
      group.leads.map((lead) => String(lead.leadId)),
    );

    setSelectedGroups((prev) => [
      ...prev,
      {
        ...group,
        selectedLeadIds,
      },
    ]);
    setCompose({ ...compose, to: "" });
    setGroupSuggestions([]);
    setShowSuggestions(false);
    setActiveGroupId(String(group.groupId));
    setGroupLeadSearch("");
  };

  const handleGroupRemove = (e, groupId) => {
    e.stopPropagation();
    setSelectedGroups((prev) =>
      prev.filter((group) => String(group.groupId) !== String(groupId)),
    );
    if (String(activeGroupId) === String(groupId)) {
      setActiveGroupId(null);
      setGroupLeadSearch("");
    }
  };

  const updateGroupLeadSelection = (groupId, getNextSelectedLeadIds) => {
    setSelectedGroups((prev) =>
      prev.map((group) => {
        if (String(group.groupId) !== String(groupId)) return group;

        return {
          ...group,
          selectedLeadIds: getNextSelectedLeadIds(group),
        };
      }),
    );
  };

  const handleLeadToggle = (groupId, leadId) => {
    updateGroupLeadSelection(groupId, (group) => {
      const nextSelectedLeadIds = new Set(group.selectedLeadIds);
      const normalizedLeadId = String(leadId);

      if (nextSelectedLeadIds.has(normalizedLeadId)) {
        nextSelectedLeadIds.delete(normalizedLeadId);
      } else {
        nextSelectedLeadIds.add(normalizedLeadId);
      }

      return nextSelectedLeadIds;
    });
  };

  const handleSelectAllLeads = (groupId) => {
    updateGroupLeadSelection(
      groupId,
      (group) => new Set(group.leads.map((lead) => String(lead.leadId))),
    );
  };

  const handleDeselectAllLeads = (groupId) => {
    updateGroupLeadSelection(groupId, () => new Set());
  };

  const resolveRecipientEmails = () => {
    const uniqueEmails = new Set();

    selectedGroups.forEach((group) => {
      group.leads.forEach((lead) => {
        if (
          lead.email &&
          group.selectedLeadIds.has(String(lead.leadId))
        ) {
          uniqueEmails.add(lead.email.trim().toLowerCase());
        }
      });
    });

    return Array.from(uniqueEmails);
  };

  // ✅ Handle SMTP form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.smtpServer || !form.senderEmail || !form.smtpPort || !form.userName || !form.senderName || !form.password) {
      setPopup({ message: "All fields are required to save SMTP credentials.", type: "error" });
      return;
    }

    try {
      const payload = {
        SmtpServer: form.smtpServer,
        SmtpPort: parseInt(form.smtpPort, 10) || 0,
        SenderEmail: form.senderEmail,
        UserName: form.userName,
        SenderName: form.senderName,
        EncryptedPassword: encryptPassword(form.password),
        IsDefault: !!form.isDefault,
        IsActive: form.isActive !== undefined ? !!form.isActive : true,
      };

      const response = await EmailService.createSmtpConfiguration(payload);
      
      if (response.status === 200 || response.status === 201 || response.data?.success !== false) {
        setPopup({ message: "SMTP configuration created successfully!", type: "success" });
        
        const createdConfig = response.data?.data ?? response.data;
        const newId = createdConfig?.id ?? createdConfig?.Id;
        
        if (newId) {
          await fetchSmtpConfigs(newId);
        } else {
          await fetchSmtpConfigs();
        }
      } else {
        setPopup({ message: "Failed to create SMTP configuration.", type: "error" });
      }
    } catch (error) {
      console.error("Error creating SMTP configuration:", error);
      const errorMsg = getApiErrorMessage(error) || "Failed to create SMTP configuration.";
      setPopup({ message: errorMsg, type: "error" });
    }
  };

  const hasSmtpCredentials = () =>
    Boolean(form.smtpServer && form.senderEmail && form.smtpPort && form.userName && form.senderName && form.password);

  // ✅ Send Email function
  const handleSendEmail = async () => {
    const recipientEmails = resolveRecipientEmails();
    const recipientEmailText = recipientEmails.join(",");

    if (recipientEmails.length === 0 || !compose.subject || !compose.body) {
      setPopup({
        message: "Please fill in all fields before sending.",
        type: "error",
      });
      return;
    }

    if (selectedProvider === GMAIL_PROVIDER || selectedProvider === ZOHO_PROVIDER) {
      const selectedProviderToken = getSelectedProviderToken();

      if (!selectedProviderToken) {
        setPopup({
          message: `Please login with ${selectedProvider} first.`,
          type: "error",
        });
        return;
      }

      try {
        const response =
          selectedProvider === GMAIL_PROVIDER
            ? await EmailService.sendGmail(
                selectedProviderToken,
                recipientEmailText,
                compose.subject,
                compose.body
              )
            : await EmailService.sendZoho(
                selectedProviderToken,
                recipientEmailText,
                compose.subject,
                compose.body
              );

        if (response.data?.success === false) {
          throw response.data;
        }

        console.log("Email sent:", response.data);
        setPopup({ message: "Email sent successfully!", type: "success" });

        // ✅ clear form after send
        setCompose({ to: "", subject: "", body: "" });
        setSelectedGroups([]);
        setActiveGroupId(null);
        setGroupLeadSearch("");
      } catch (error) {
        console.error("Error sending email:", error);

        if (isEmailUnauthorizedError(error)) {
          if (selectedProvider === GMAIL_PROVIDER) {
            disconnectGmailIntegration();
          }

          if (selectedProvider === ZOHO_PROVIDER) {
            disconnectZohoIntegration();
          }

          setPopup({
            message:
              `Your ${selectedProvider} session has expired. Please reconnect ${selectedProvider} to send emails.`,
            type: "error",
          });
          return;
        }

        setPopup({ message: "Failed to send email.", type: "error" });
      }
    } else {
      if (!selectedSmtpConfigId) {
        setPopup({
          message: "Please select or create an SMTP configuration before sending.",
          type: "error",
        });
        return;
      }

      try {
        const response = await EmailService.sendSmtp(
          recipientEmailText,
          compose.subject,
          compose.body,
          []
        );

        if (response.data?.success === false) {
          throw response.data;
        }

        console.log("Email sent:", response.data);
        setPopup({ message: "Email sent successfully!", type: "success" });

        // ✅ clear form after send
        setCompose({ to: "", subject: "", body: "" });
        setSelectedGroups([]);
        setActiveGroupId(null);
        setGroupLeadSearch("");
      } catch (error) {
        console.error("Error sending SMTP email:", error);
        setPopup({ message: "Failed to send email.", type: "error" });
      }
    }
  };

  const activeGroup = selectedGroups.find(
    (group) => String(group.groupId) === String(activeGroupId),
  );
  const filteredActiveGroupLeads = activeGroup
    ? activeGroup.leads.filter((lead) => {
        const searchText = groupLeadSearch.trim().toLowerCase();
        if (!searchText) return true;

        return (
          lead.leadName.toLowerCase().includes(searchText) ||
          lead.email.toLowerCase().includes(searchText)
        );
      })
    : [];

  return (
    <div className="mail-setup-wrapper">
      <h2 className="section-title">Integrate your mail</h2>
      <p className="section-subtitle">
        Send emails through your business email account to your leads.
      </p>

      {/* Mail Providers */}
      <div className="mail-providers">
        {EMAIL_PROVIDERS.map((provider) => (
          <div
            className={`provider-item ${
              selectedProvider === provider ? "selected" : ""
            } ${integratedProviders.has(provider) ? "active-green" : ""}`}
            key={provider}
            onClick={() => handleProviderClick(provider)}
          >
            {integratedProviders.has(provider) && (
              <button
                type="button"
                className="remove-integration-badge"
                aria-label={`Remove ${provider} integration`}
                title={`Remove ${provider} integration`}
                onClick={(e) => handleRemoveIntegration(e, provider)}
              >
                ×
              </button>
            )}
            <div className="provider-circle">
              {provider === ZOHO_PROVIDER ? (
                <span className="provider-logo-fallback">Z</span>
              ) : (
                <img
                  src={`../assets/images/${PROVIDER_IMAGE_BY_PROVIDER[provider]}.png`}
                  alt={provider}
                  style={{ maxHeight: "40px" }}
                />
              )}
            </div>
            <span>{provider}</span>
          </div>
        ))}
      </div>

      {/* SMTP Provider setup and configs selection (only for Other) */}
      {selectedProvider === SMTP_PROVIDER && (
        <div className="smtp-setup-section">
          <div className="smtp-config-selector-bar">
            <div className="selector-field">
              <label htmlFor="smtp-config-select">Select SMTP Configuration:</label>
              {isLoadingConfigs ? (
                <span className="loading-text">Loading configurations...</span>
              ) : (
                <select
                  id="smtp-config-select"
                  className="smtp-config-dropdown"
                  value={selectedSmtpConfigId}
                  onChange={(e) => handleSmtpConfigSelect(e.target.value)}
                >
                  <option value="">-- Choose an SMTP Configuration --</option>
                  {smtpConfigs.map((cfg) => {
                    const cfgId = cfg.id ?? cfg.Id;
                    const email = cfg.senderEmail ?? cfg.SenderEmail;
                    const server = cfg.smtpServer ?? cfg.SmtpServer;
                    const isDef = cfg.isDefault ?? cfg.IsDefault;
                    return (
                      <option key={cfgId} value={cfgId}>
                        {email} ({server}) {isDef ? "[Default]" : ""}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
            <button
              type="button"
              className="add-new-smtp-btn"
              onClick={handleAddNewSmtpClick}
            >
              + Add New SMTP Configuration
            </button>
          </div>

          {(showSmtpForm || selectedSmtpConfigId) && (
            <form className="smtp-form" onSubmit={handleSubmit}>
              <h4 className="form-section-title">
                {showSmtpForm ? "Add New SMTP Configuration" : "SMTP Configuration Details"}
              </h4>
              <div className="form-grid">
                <div className="input-group">
                  <label>SMTP Server</label>
                  <input
                    type="text"
                    name="smtpServer"
                    placeholder="SMTP server"
                    value={form.smtpServer}
                    onChange={handleChange}
                    disabled={!showSmtpForm}
                  />
                </div>
                <div className="input-group">
                  <label>Sender Email</label>
                  <input
                    type="email"
                    name="senderEmail"
                    placeholder="Sender email"
                    value={form.senderEmail}
                    onChange={handleChange}
                    disabled={!showSmtpForm}
                  />
                </div>
                <div className="input-group">
                  <label>SMTP Port</label>
                  <input
                    type="number"
                    name="smtpPort"
                    placeholder="SMTP port"
                    value={form.smtpPort}
                    onChange={handleChange}
                    disabled={!showSmtpForm}
                  />
                </div>
                <div className="input-group">
                  <label>User Name</label>
                  <input
                    type="text"
                    name="userName"
                    placeholder="User name"
                    value={form.userName}
                    onChange={handleChange}
                    disabled={!showSmtpForm}
                  />
                </div>
                <div className="input-group">
                  <label>Sender Name</label>
                  <input
                    type="text"
                    name="senderName"
                    placeholder="Sender name"
                    value={form.senderName}
                    onChange={handleChange}
                    disabled={!showSmtpForm}
                  />
                </div>
                <div className="input-group">
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={form.password}
                    onChange={handleChange}
                    disabled={!showSmtpForm}
                  />
                </div>
              </div>

              <div className="form-checkbox-row">
                <label className="form-checkbox-label">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={form.isDefault}
                    onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                    disabled={!showSmtpForm}
                  />
                  <span>Mark as Default SMTP configuration</span>
                </label>
              </div>

              {showSmtpForm && (
                <div className="form-submit">
                  <button type="submit" className="save-credentials-btn">
                    Save Credentials
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      )}

      {/* ✅ Compose Email (shown for all providers) */}
      {selectedProvider && (
        <div className="compose-email-section">
          <div className="compose-header">
            <h3>Compose Emails ({selectedProvider})</h3>
            <button className="all-leads-btn">Send to All leads</button>
          </div>

          <div className="compose-box">
            <div className="compose-to">
              <label>To:</label>
              <div className="recipient-autocomplete">
                <div
                  className="recipient-token-input"
                  onClick={() => recipientInputRef.current?.focus()}
                >
                  {selectedGroups.map((group) => (
                    <button
                      type="button"
                      className={`recipient-group-token ${
                        String(activeGroupId) === String(group.groupId)
                          ? "active"
                          : ""
                      }`}
                      key={group.groupId}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveGroupId(
                          String(activeGroupId) === String(group.groupId)
                            ? null
                            : String(group.groupId),
                        );
                        setGroupLeadSearch("");
                      }}
                    >
                      <span>{group.groupName}</span>
                      <span className="recipient-group-token-count">
                        {group.selectedLeadIds.size}/{group.leads.length}
                      </span>
                      <span
                        className="recipient-group-token-remove"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleGroupRemove(e, group.groupId)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleGroupRemove(e, group.groupId);
                          }
                        }}
                      >
                        x
                      </span>
                    </button>
                  ))}

                  <input
                    ref={recipientInputRef}
                    className="recipient-search-input"
                    type="text"
                    placeholder={
                      selectedGroups.length > 0
                        ? "Add another group"
                        : "Search group"
                    }
                    value={compose.to}
                    onChange={handleRecipientChange}
                    onFocus={() => {
                      if (compose.to.trim().length >= 3) {
                        setShowSuggestions(true);
                      }
                    }}
                    autoComplete="off"
                  />
                </div>

                {showSuggestions &&
                  (groupSuggestions.length > 0 || isSuggestionLoading) && (
                    <div className="lead-suggestions-dropdown">
                      {isSuggestionLoading ? (
                        <div className="lead-suggestion-status">
                          Searching groups...
                        </div>
                      ) : (
                        groupSuggestions.map((suggestion) => (
                          <button
                            type="button"
                            className="lead-suggestion-item"
                            key={suggestion.groupId}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleGroupSelect(suggestion)}
                          >
                            <span className="lead-suggestion-name">
                              {suggestion.groupName}
                            </span>
                            <span className="lead-suggestion-email">
                              {suggestion.leads.length} leads
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                {activeGroup && (
                  <div className="group-leads-panel">
                    <div className="group-leads-panel-header">
                      <div>
                        <strong>{activeGroup.groupName}</strong>
                        <span>
                          {activeGroup.selectedLeadIds.size} of{" "}
                          {activeGroup.leads.length} selected
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveGroupId(null);
                          setGroupLeadSearch("");
                        }}
                      >
                        x
                      </button>
                    </div>

                    <input
                      className="group-lead-search"
                      type="text"
                      placeholder="Search leads"
                      value={groupLeadSearch}
                      onChange={(e) => setGroupLeadSearch(e.target.value)}
                    />

                    <div className="group-leads-actions">
                      <button
                        type="button"
                        onClick={() => handleSelectAllLeads(activeGroup.groupId)}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleDeselectAllLeads(activeGroup.groupId)
                        }
                      >
                        Deselect all
                      </button>
                    </div>

                    <div className="group-leads-list">
                      {filteredActiveGroupLeads.length > 0 ? (
                        filteredActiveGroupLeads.map((lead) => (
                          <label
                            className="group-lead-option"
                            key={`${activeGroup.groupId}-${lead.leadId}`}
                          >
                            <input
                              type="checkbox"
                              checked={activeGroup.selectedLeadIds.has(
                                String(lead.leadId),
                              )}
                              onChange={() =>
                                handleLeadToggle(
                                  activeGroup.groupId,
                                  lead.leadId,
                                )
                              }
                            />
                            <span>
                              <strong>{lead.leadName || lead.email}</strong>
                              <small>{lead.email}</small>
                            </span>
                          </label>
                        ))
                      ) : (
                        <div className="lead-suggestion-status">
                          No leads found.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="compose-to">
              <label>Subject:</label>
              <input
                type="text"
                placeholder="Enter subject"
                value={compose.subject}
                onChange={(e) =>
                  setCompose({ ...compose, subject: e.target.value })
                }
              />
            </div>

            <div className="compose-to">
              <label>Message:</label>
              <textarea
                rows="8"
                placeholder="Enter your message"
                value={compose.body}
                onChange={(e) =>
                  setCompose({ ...compose, body: e.target.value })
                }
              ></textarea>
            </div>

            <button className="send-btn" onClick={handleSendEmail}>
              Send
            </button>
          </div>
        </div>
      )}

      {/* ✅ Common PopupMessage */}
      <PopupMessage
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ message: "", type: "success" })}
      />
    </div>
  );
};

export default EmailSetup;
